import { useNavigate } from "react-router-dom";
import { AlertCircle, Home, ArrowLeft, Search, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

export default function ErrorPage() {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
            <style jsx global>{`
                * {
                    font-family: Arial, Helvetica, sans-serif;
                }
            `}</style>

            <Card className="max-w-2xl w-full shadow-2xl border-0">
                <CardContent className="p-8 md:p-12">
                    <div className="text-center">
                        {/* 404 Graphic */}
                        <div className="relative mb-8">
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-48 h-48 bg-gradient-to-br from-[#12006d]/10 to-[#4fcdfa]/10 rounded-full blur-3xl"></div>
                            </div>
                            <div className="relative">
                                <h1 className="text-8xl md:text-9xl font-bold text-transparent bg-gradient-to-br from-[#12006d] to-[#4fcdfa] bg-clip-text mb-2">
                                    404
                                </h1>
                                <div className="flex items-center justify-center gap-2 text-[#12006d]/60">
                                    <AlertCircle className="w-6 h-6" />
                                    <span className="text-lg font-medium">Page Not Found</span>
                                </div>
                            </div>
                        </div>

                        {/* Message */}
                        <div className="mb-8">
                            <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-3">
                                Oops! We can't find that page
                            </h2>
                            <p className="text-slate-600 text-base md:text-lg leading-relaxed">
                                The page you're looking for doesn't exist or has been moved. 
                                Let's get you back on track.
                            </p>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                            <Button
                                onClick={() => navigate(-1)}
                                variant="outline"
                                size="lg"
                                className="rounded-full px-6 gap-2"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                Go Back
                            </Button>
                            
                            <Button
                                onClick={() => navigate('/')}
                                size="lg"
                                className="rounded-full px-6 gap-2 bg-[#12006d] hover:bg-[#1a0090]"
                            >
                                <Home className="w-4 h-4" />
                                Go Home
                            </Button>
                        </div>

                        {/* Quick Links */}
                        <div className="pt-8 border-t border-slate-200">
                            <p className="text-sm text-slate-500 mb-4">Quick Links</p>
                            <div className="flex flex-wrap gap-3 justify-center">
                                <Button
                                    onClick={() => navigate('/dashboard')}
                                    variant="ghost"
                                    size="sm"
                                    className="text-[#12006d] hover:text-[#4fcdfa] hover:bg-[#4fcdfa]/10"
                                >
                                    Dashboard
                                </Button>
                                <Button
                                    onClick={() => navigate('/calendar')}
                                    variant="ghost"
                                    size="sm"
                                    className="text-[#12006d] hover:text-[#4fcdfa] hover:bg-[#4fcdfa]/10"
                                >
                                    Calendar
                                </Button>
                                <Button
                                    onClick={() => navigate('/clients')}
                                    variant="ghost"
                                    size="sm"
                                    className="text-[#12006d] hover:text-[#4fcdfa] hover:bg-[#4fcdfa]/10"
                                >
                                    Clients
                                </Button>
                                <Button
                                    onClick={() => navigate('/settings')}
                                    variant="ghost"
                                    size="sm"
                                    className="text-[#12006d] hover:text-[#4fcdfa] hover:bg-[#4fcdfa]/10"
                                >
                                    Settings
                                </Button>
                            </div>
                        </div>

                        {/* Decorative Elements */}
                        <div className="mt-8 flex items-center justify-center gap-6 text-slate-300">
                            <Compass className="w-8 h-8 animate-pulse" />
                            <Search className="w-8 h-8 animate-pulse delay-75" />
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}